'use client'

import { AuthLayout } from '@/components/layout/AuthLayout'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { safeNext } from '@/lib/safe-next'
import { authClient } from '@vagahub/db/browser'
import { AlertCircle, ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import type React from 'react'
import { Suspense, useState } from 'react'

function LoginContent() {
  const searchParams = useSearchParams()
  const next = safeNext(searchParams.get('next'))
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const { error: signInError } = await authClient.signIn.email({ email, password })

      if (signInError) throw new Error(signInError.message ?? 'E-mail ou senha inválidos.')
      router.push(next)
    } catch (err) {
      let msg = err instanceof Error ? err.message : 'E-mail ou senha inválidos.'
      if (msg.includes('Email not confirmed')) {
        msg = 'E-mail não confirmado. Verifique o WhatsApp para o código de ativação.'
      } else if (msg.includes('Invalid login credentials')) {
        msg = 'E-mail ou senha inválidos.'
      }
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout
      title="Bem-vindo de volta"
      description="Acesse sua conta para continuar buscando vagas."
      footer={
        <>
          Ainda não tem conta?{' '}
          <Link
            href={`/auth/register?next=${encodeURIComponent(next)}`}
            className="text-blue-600 hover:underline font-semibold"
          >
            Cadastre-se grátis
          </Link>
        </>
      }
    >
      {error && (
        <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-100 flex items-start gap-2.5 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-300">
            E-mail
          </Label>
          <div className="input-icon-wrapper">
            <Mail className="input-icon-left" />
            <Input
              id="email"
              type="email"
              placeholder="nome@email.com"
              className="input-branded"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
        </div>

        {/* Senha */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="password"
              className="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              Senha
            </Label>
            <Link href="/auth/recover" className="text-xs text-blue-600 hover:underline">
              Esqueceu?
            </Link>
          </div>
          <div className="input-icon-wrapper">
            <Lock className="input-icon-left" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="input-branded pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-brand w-full h-11 flex items-center justify-center gap-2 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg
                className="animate-spin h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Entrando...
            </span>
          ) : (
            <>
              Entrar
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </AuthLayout>
  )
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginContent />
    </Suspense>
  )
}
